import { useState } from "react";
import { Box, Button, Code, Flex, IconButton, Text, TextField } from "@radix-ui/themes";
import { Image as ImageIcon, Link2, X } from "lucide-react";
import { FilePickerDialog } from "@/components/dashboard/FilePickerDialog";
import { File as TenantFile } from "@/types/breeze/File";

interface Props {
  value: string;
  onChange: (next: string) => void;
  label?: string;
  description?: React.ReactNode;
  allowManualUrl?: boolean;
  urlPlaceholder?: string;
}

export default function ImagePickerField({
  value,
  onChange,
  label,
  description,
  allowManualUrl = true,
  urlPlaceholder = "https://cdn.example.com/banner.jpg",
}: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const looksLikeTemplate = value.includes("{{");
  const [manualOpen, setManualOpen] = useState(looksLikeTemplate);

  return (
    <Box>
      {label && (
        <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
          {label}
        </Text>
      )}

      {value ? (
        <Box
          style={{
            border: "1px solid var(--gray-5)",
            borderRadius: "var(--radius-3)",
            overflow: "hidden",
            background: "var(--gray-2)",
          }}
        >
          <Box
            style={{
              width: "100%",
              aspectRatio: "16 / 9",
              maxHeight: 240,
              backgroundImage: `url("${value}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundColor: "var(--gray-3)",
            }}
            aria-label="Selected image preview"
          />
          <Flex justify="between" align="center" p="2" style={{ borderTop: "1px solid var(--gray-5)" }}>
            <Box style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
              <Text size="1" color="gray" truncate style={{ display: "block" }}>
                {value}
              </Text>
            </Box>
            <Flex gap="2">
              <Button
                type="button"
                variant="soft"
                size="1"
                onClick={() => setPickerOpen(true)}
              >
                <ImageIcon size={12} /> Change
              </Button>
              <IconButton
                type="button"
                variant="soft"
                color="red"
                size="1"
                onClick={() => onChange("")}
                title="Remove image"
              >
                <X size={12} />
              </IconButton>
            </Flex>
          </Flex>
        </Box>
      ) : (
        <Flex gap="2" align="center" wrap="wrap">
          <Button
            type="button"
            variant="soft"
            onClick={() => setPickerOpen(true)}
          >
            <ImageIcon size={14} /> Pick or upload an image
          </Button>
          {allowManualUrl && !manualOpen && (
            <Button
              type="button"
              variant="ghost"
              color="gray"
              onClick={() => setManualOpen(true)}
            >
              <Link2 size={14} /> Or use a URL / variable
            </Button>
          )}
        </Flex>
      )}

      {allowManualUrl && manualOpen && (
        <Box mt="2">
          <Flex justify="between" align="end" mb="1">
            <Text size="1" color="gray">Image URL or templated expression</Text>
            {!value && (
              <Button
                type="button"
                variant="ghost"
                size="1"
                color="gray"
                onClick={() => setManualOpen(false)}
              >
                Hide
              </Button>
            )}
          </Flex>
          <TextField.Root
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={urlPlaceholder}
          />
          <Text size="1" color="gray" mt="1" style={{ display: "block" }}>
            Use <Code size="1" variant="ghost">{`{{ $anythink.trigger.data.x }}`}</Code> to pull from the trigger payload.
          </Text>
        </Box>
      )}

      {description && (
        <Box mt="2">
          <Text size="1" color="gray" style={{ display: "block" }}>{description}</Text>
        </Box>
      )}

      <FilePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={(file: TenantFile) => onChange(file.cdn_url)}
        restrictedType="image"
        title="Pick a notification image"
        description="Choose an existing image from your file storage, or upload a new one."
      />
    </Box>
  );
}
